/**
 * Sector operations — Construction Helper DPR section (R39 B9.b1).
 * Mobile-first DPR daily report form for phone width (375 px).
 * Offline-first drafts via queueService / sharedDeviceWorkRouter.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useWorkspace } from '../../../context/useWorkspace'
import { Button, Select, StatusPill, TextInput } from '../../Common'
import type { SelectOption } from '../../Common'
import { BarcodeScannerShell } from '../../Helper/BarcodeScannerShell'
import {
  getDpr,
  submitDpr,
} from '../../../services/conDprService'
import { listConProjects } from '../../../services/conProjectsService'
import { queueOpsDprSave } from '../../../services/helper/queueService'
import { createOpId } from '../../../services/helper/opId'
import {
  getSharedDeviceWorkRouterContext,
  routeDprSave,
} from '../../../services/sharedDeviceWorkRouter'
import { workspaceTodayKey } from '../../../services/repeat/keys'
import { unitSubtreeIds } from '../hospital/hospitalTodayHelpers'
import { resolvePlaceToken } from '../../../services/opsPlacesService'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { isNetworkError } from '../../../services/authRevocation'
import {
  buildConDprRecordId,
  type ConProject,
  type ConDprShift,
  type ConDprLine,
  type ConDprBlock,
  type OpsPlace,
  type OpsRecord,
} from '../../../types/ops'
import styles from './DprPhoneSection.module.css'

export interface DprPhoneSectionProps {
  workspaceId: string
  callerUid: string
  callerRole?: string | null
  places: OpsPlace[]
  isSharedDevice?: boolean
  onError?: (err: string | null) => void
}

interface DprCacheEntry {
  record: OpsRecord | null
  dpr: ConDprBlock | null
  canEdit: boolean
  canSubmit: boolean
  lines: ConDprLine[]
}

const dprCache = new Map<string, DprCacheEntry>()

function createLocalLineId(): string {
  return createOpId()
}

export function DprPhoneSection({
  workspaceId,
  places,
  onError,
}: DprPhoneSectionProps): React.ReactElement {
  const { t } = useNls()
  const { workspaces } = useWorkspace()
  const workspace = workspaces.find((w) => w.id === workspaceId)
  const todayKey = useMemo(
    () => workspaceTodayKey('DAILY', workspace?.timezone),
    [workspace?.timezone],
  )

  const [projects, setProjects] = useState<ConProject[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedShift, setSelectedShift] = useState<ConDprShift>('DAY')

  // DPR server data
  const [dprRecord, setDprRecord] = useState<OpsRecord | null>(null)
  const [canEdit, setCanEdit] = useState<boolean>(false)
  const [canSubmit, setCanSubmit] = useState<boolean>(false)
  const [lines, setLines] = useState<ConDprLine[]>([])
  const [lineBlobs, setLineBlobs] = useState<Array<{ lineId: string; blob: Blob; previewUrl: string }>>([])
  const [busy, setBusy] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [statusMsg, setStatusMsg] = useState<string | null>(null)
  const [refreshNonce, setRefreshNonce] = useState<number>(0)

  // Scanner state for lines
  const [activeScanLineId, setActiveScanLineId] = useState<string | null>(null)

  // Live online state
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  // Load projects
  useEffect(() => {
    let ignore = false
    if (!workspaceId) return
    void listConProjects({ workspaceId, status: 'ACTIVE' })
      .then((res) => {
        if (ignore) return
        const activeProjects = res.projects || []
        setProjects(activeProjects)
        if (activeProjects.length > 0 && !selectedProjectId) {
          const firstProj = activeProjects[0]
          if (firstProj?.id) {
            setSelectedProjectId(firstProj.id)
          }
        }
      })
      .catch((err: unknown) => {
        if (ignore) return
        const msg = getApiErrorMessage(err)
        setErrorMsg(msg)
        onError?.(msg)
      })
    return () => {
      ignore = true
    }
  }, [workspaceId, selectedProjectId, onError])

  const selectedProject = useMemo(() => {
    return projects.find((p) => p.id === selectedProjectId) || null
  }, [projects, selectedProjectId])

  const selectedProjectPlaceId = selectedProject?.placeId

  // Allowed subtree places
  const allowedPlaceIds = useMemo(() => {
    if (!selectedProjectPlaceId) return new Set<string>()
    return unitSubtreeIds(places, selectedProjectPlaceId)
  }, [places, selectedProjectPlaceId])

  const projectPlaces = useMemo(() => {
    const list = places.filter((p) => !p.archived && allowedPlaceIds.has(p.id || ''))
    return list.length > 0 ? list : places.filter((p) => !p.archived)
  }, [places, allowedPlaceIds])

  const placeOptions: SelectOption<string>[] = useMemo(() => {
    return projectPlaces.map((p) => ({
      value: p.id || '',
      label: `${p.name} (${p.level})`,
    }))
  }, [projectPlaces])

  const packageOptions: SelectOption<string>[] = useMemo(() => {
    const noneLabel = t('NLSHelperOpsDprNone') || '(None)'
    if (!selectedProject) return [{ value: '', label: noneLabel }]
    return [
      { value: '', label: noneLabel },
      ...selectedProject.packages
        .filter((pkg) => !pkg.archived)
        .map((pkg) => ({
          value: pkg.key,
          label: `${pkg.name} (${pkg.trade})`,
        })),
    ]
  }, [selectedProject, t])

  const shiftOptions: SelectOption<ConDprShift>[] = useMemo(
    () => [
      { value: 'DAY', label: t('NLSHelperOpsDprShiftDay') || 'Day' },
      { value: 'NIGHT', label: t('NLSHelperOpsDprShiftNight') || 'Night' },
      { value: 'SINGLE', label: t('NLSHelperOpsDprShiftSingle') || 'Single' },
    ],
    [t],
  )

  const currentDprId = useMemo(() => {
    if (!selectedProjectId || !todayKey) return ''
    return buildConDprRecordId(selectedProjectId, todayKey, selectedShift)
  }, [selectedProjectId, todayKey, selectedShift])

  // Single loader with refreshNonce
  useEffect(() => {
    let ignore = false
    if (!workspaceId || !currentDprId) return

    void getDpr({ workspaceId, recordId: currentDprId })
      .then((res) => {
        if (ignore) return
        setErrorMsg(null)
        setDprRecord(res.record)
        setCanEdit(Boolean(res.canEdit))
        setCanSubmit(Boolean(res.canSubmit))
        const loadedLines = res.dpr?.lines && res.dpr.lines.length > 0 ? res.dpr.lines : []
        setLines(loadedLines)
        dprCache.set(currentDprId, {
          record: res.record,
          dpr: res.dpr,
          canEdit: Boolean(res.canEdit),
          canSubmit: Boolean(res.canSubmit),
          lines: loadedLines,
        })
      })
      .catch((err: unknown) => {
        if (ignore) return
        const msg = getApiErrorMessage(err)
        setErrorMsg(msg)
        onError?.(msg)

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
        const isOfflineOrNetwork = isOffline || isNetworkError(err)

        const cached = dprCache.get(currentDprId)
        if (isOfflineOrNetwork) {
          if (cached) {
            setDprRecord(cached.record)
            setCanEdit(cached.canEdit)
            setCanSubmit(cached.canSubmit)
            setLines(cached.lines)
          } else {
            setDprRecord(null)
            setCanEdit(false)
            setCanSubmit(false)
            setLines([])
            if (isOffline) {
              const offlineHint = t('NLSHelperOpsDprOfflineNoCacheHint') || 'Offline: no cached copy available'
              setErrorMsg(`${msg} — ${offlineHint}`)
            }
          }
        } else {
          // Any other error (e.g. permission-denied, not-found, failed-precondition while online)
          // Show the error, flags false, inputs read-only. A cached record may still be shown read-only.
          setCanEdit(false)
          setCanSubmit(false)
          if (cached) {
            setDprRecord(cached.record)
            setLines(cached.lines)
          } else {
            setDprRecord(null)
            setLines([])
          }
        }
      })
      .finally(() => {
        if (!ignore) setBusy(false)
      })

    return () => {
      ignore = true
    }
  }, [workspaceId, currentDprId, refreshNonce, onError, t])

  const handleAddLine = () => {
    if (!canEdit) return
    const newLine: ConDprLine = {
      lineId: createLocalLineId(),
      placeId: selectedProject?.placeId || projectPlaces[0]?.id || '',
      trade: '',
      activity: '',
      plannedQty: null,
      doneQty: 0,
      unit: '',
      gangs: [],
      equipment: [],
      photoPaths: [],
    }
    setLines((prev) => [...prev, newLine])
  }

  const handleUpdateLine = (lineId: string, updates: Partial<ConDprLine>) => {
    if (!canEdit) return
    setLines((prev) =>
      prev.map((l) => (l.lineId === lineId ? { ...l, ...updates } : l)),
    )
  }

  const handleRemoveLine = (lineId: string) => {
    if (!canEdit) return
    setLines((prev) => prev.filter((l) => l.lineId !== lineId))
    setLineBlobs((prev) => prev.filter((b) => b.lineId !== lineId))
  }

  const handleScanPlace = async (scannedText: string) => {
    if (!activeScanLineId) return
    let token = scannedText.trim()
    if (token.includes('/pq/')) {
      token = token.split('/pq/')[1]?.split('?')[0] || token
    } else if (token.includes('/q/')) {
      token = token.split('/q/')[1]?.split('?')[0] || token
    }
    const lineId = activeScanLineId
    setActiveScanLineId(null)

    const directMatch = places.find((p) => p.id === token)
    if (directMatch?.id) {
      if (allowedPlaceIds.has(directMatch.id)) {
        handleUpdateLine(lineId, { placeId: directMatch.id })
      } else {
        const msg = t('NLSHelperOpsDprPlaceOutsideSite') || 'Place is outside project site'
        setErrorMsg(msg)
        onError?.(msg)
      }
      return
    }

    try {
      const res = await resolvePlaceToken({ token })
      if (res?.placeId) {
        if (allowedPlaceIds.has(res.placeId)) {
          handleUpdateLine(lineId, { placeId: res.placeId })
        } else {
          const msg = t('NLSHelperOpsDprPlaceOutsideSite') || 'Place is outside project site'
          setErrorMsg(msg)
          onError?.(msg)
        }
      } else {
        const msg = t('NLSHelperOpsDprPlaceNotFound') || 'Place not found'
        setErrorMsg(msg)
        onError?.(msg)
      }
    } catch {
      const msg = t('NLSHelperOpsDprPlaceNotFound') || 'Place not found'
      setErrorMsg(msg)
      onError?.(msg)
    }
  }

  const handleAddPhoto = (lineId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) return
    const files = e.target.files
    if (!files || files.length === 0) return

    const line = lines.find((l) => l.lineId === lineId)
    const existingCount = (line?.photoPaths?.length || 0) + lineBlobs.filter((b) => b.lineId === lineId).length
    const availableSlots = Math.max(0, 20 - existingCount)

    const newBlobs: Array<{ lineId: string; blob: Blob; previewUrl: string }> = []
    for (let i = 0; i < Math.min(files.length, availableSlots); i++) {
      const f = files[i]
      if (f) {
        const previewUrl = URL.createObjectURL(f)
        newBlobs.push({ lineId, blob: f, previewUrl })
      }
    }
    if (newBlobs.length > 0) {
      setLineBlobs((prev) => [...prev, ...newBlobs])
    }
  }

  const handleSaveDraft = async () => {
    if (!canEdit || !selectedProjectId) return
    setBusy(true)
    setErrorMsg(null)
    setStatusMsg(null)

    const opId = createOpId()
    const lineFilesToStore = lineBlobs.map((lb) => ({
      lineId: lb.lineId,
      file: lb.blob,
    }))

    const payload = {
      workspaceId,
      projectId: selectedProjectId,
      reportDate: todayKey,
      shift: selectedShift,
      lines,
      opId,
      clientMutationId: opId,
    }

    try {
      const routerCtx = getSharedDeviceWorkRouterContext()
      if (routerCtx) {
        if (lineFilesToStore.length > 0) {
          await routeDprSave(payload, routerCtx, undefined, lineFilesToStore)
        } else {
          await routeDprSave(payload, routerCtx)
        }
      } else {
        if (lineFilesToStore.length > 0) {
          await queueOpsDprSave(payload, undefined, lineFilesToStore)
        } else {
          await queueOpsDprSave(payload)
        }
      }
      setStatusMsg(t('NLSHelperOpsDprDraftSaved'))
      setLineBlobs([])
      setRefreshNonce((n) => n + 1)
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err)
      setErrorMsg(msg)
      onError?.(msg)
    } finally {
      setBusy(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || !isOnline || !selectedProjectId) return
    setBusy(true)
    setErrorMsg(null)
    setStatusMsg(null)

    try {
      await submitDpr({
        workspaceId,
        projectId: selectedProjectId,
        reportDate: todayKey,
        shift: selectedShift,
        lines,
      })
      setStatusMsg(t('NLSHelperOpsDprSubmitted'))
      setRefreshNonce((n) => n + 1)
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err)
      setErrorMsg(msg)
      onError?.(msg)
    } finally {
      setBusy(false)
    }
  }

  const projectSelectOptions: SelectOption<string>[] = useMemo(() => {
    return projects.map((p) => ({
      value: p.id,
      label: `${p.name} (${p.code})`,
    }))
  }, [projects])

  return (
    <div className={styles.section} data-testid="dpr-phone-section">
      <div className={styles.sectionHeader}>
        <div className={styles.titleGroup}>
          <h2 className={styles.sectionTitle}>{t('NLSHelperOpsDprTitle')}</h2>
          <p className={styles.sectionDesc}>{t('NLSHelperOpsDprDesc')}</p>
        </div>
        {dprRecord?.state ? (
          <StatusPill
            tone={dprRecord.state === 'APPROVED' ? 'success' : dprRecord.state === 'RETURNED' ? 'danger' : 'info'}
          >
            {dprRecord.state}
          </StatusPill>
        ) : null}
      </div>

      {errorMsg ? (
        <div className={styles.errorBanner} data-testid="dpr-error-banner" role="alert">
          {errorMsg}
        </div>
      ) : null}

      {statusMsg ? (
        <div className={styles.statusRow}>
          <span className={styles.statusValue}>{statusMsg}</span>
        </div>
      ) : null}

      {dprRecord?.state === 'RETURNED' && dprRecord.returnedReason ? (
        <div className={styles.returnNote} data-testid="dpr-return-note">
          <strong>{t('NLSHelperOpsDprReturnedTitle')}:</strong> {dprRecord.returnedReason}
        </div>
      ) : null}

      <div className={styles.controlsRow}>
        <div className={styles.inputGroup}>
          <label className={styles.fieldLabel} htmlFor="dpr-project-select">
            {t('NLSHelperOpsDprProjectLabel')}
          </label>
          <Select
            id="dpr-project-select"
            value={selectedProjectId}
            onChange={(val) => setSelectedProjectId(val)}
            options={projectSelectOptions}
            disabled={busy || projects.length === 0}
            ariaLabel={t('NLSHelperOpsDprProjectLabel')}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.fieldLabel} htmlFor="dpr-shift-select">
            {t('NLSHelperOpsDprShiftLabel')}
          </label>
          <Select
            id="dpr-shift-select"
            value={selectedShift}
            onChange={(val) => setSelectedShift(val as ConDprShift)}
            options={shiftOptions}
            disabled={busy}
            ariaLabel={t('NLSHelperOpsDprShiftLabel')}
          />
        </div>
      </div>

      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>{t('NLSHelperOpsDprDateLabel')}:</span>
        <span className={styles.statusValue}>{todayKey}</span>
        <span className={styles.statusLabel}>•</span>
        <span className={styles.statusLabel}>{t('NLSHelperOpsDprShiftLabel')}:</span>
        <span className={styles.statusValue}>{selectedShift}</span>
      </div>

      {/* Production Lines */}
      <div className={styles.linesContainer}>
        {lines.map((line, idx) => {
          const lineBlobsForThisLine = lineBlobs.filter((b) => b.lineId === line.lineId)
          const totalPhotos = (line.photoPaths?.length || 0) + lineBlobsForThisLine.length

          return (
            <div key={line.lineId} className={styles.lineCard} data-testid={`dpr-line-${line.lineId}`}>
              <div className={styles.lineHeader}>
                <span>
                  {t('NLSHelperOpsDprLineHeader', { index: idx + 1 })}
                </span>
                {canEdit && lines.length > 1 ? (
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => handleRemoveLine(line.lineId)}
                  >
                    {t('NLSHelperOpsDprRemoveLineBtn')}
                  </Button>
                ) : null}
              </div>

              <div className={styles.lineInputs}>
                {/* Place picker & Scan place */}
                <div className={styles.scanPlaceRow}>
                  <div className={styles.scanPlaceSelect}>
                    <label className={styles.fieldLabel} htmlFor={`dpr-line-place-${line.lineId}`}>
                      {t('NLSHelperOpsDprPlaceLabel')}
                    </label>
                    <Select
                      id={`dpr-line-place-${line.lineId}`}
                      value={line.placeId}
                      onChange={(val) => handleUpdateLine(line.lineId, { placeId: val })}
                      options={placeOptions}
                      disabled={!canEdit || busy}
                      ariaLabel={t('NLSHelperOpsDprPlaceLabel')}
                    />
                  </div>
                  {canEdit ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid={`dpr-scan-place-btn-${line.lineId}`}
                      onClick={() => setActiveScanLineId(line.lineId)}
                    >
                      {t('NLSHelperOpsScanPlaceBtn')}
                    </Button>
                  ) : null}
                </div>

                {/* Trade & Package */}
                <div className={styles.gridTwoCol}>
                  <div className={styles.inputGroup}>
                    <label className={styles.fieldLabel} htmlFor={`dpr-line-trade-${line.lineId}`}>
                      {t('NLSHelperOpsDprTradeLabel')}
                    </label>
                    <TextInput
                      id={`dpr-line-trade-${line.lineId}`}
                      value={line.trade}
                      onChange={(val) => handleUpdateLine(line.lineId, { trade: val })}
                      readOnly={!canEdit}
                      disabled={busy}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.fieldLabel} htmlFor={`dpr-line-package-${line.lineId}`}>
                      {t('NLSHelperOpsDprPackageLabel')}
                    </label>
                    <Select
                      id={`dpr-line-package-${line.lineId}`}
                      value={line.packageId || ''}
                      onChange={(val) => handleUpdateLine(line.lineId, { packageId: val || null })}
                      options={packageOptions}
                      disabled={!canEdit || busy}
                      ariaLabel={t('NLSHelperOpsDprPackageLabel')}
                    />
                  </div>
                </div>

                {/* Activity */}
                <div className={styles.inputGroup}>
                  <label className={styles.fieldLabel} htmlFor={`dpr-line-activity-${line.lineId}`}>
                    {t('NLSHelperOpsDprActivityLabel')}
                  </label>
                  <TextInput
                    id={`dpr-line-activity-${line.lineId}`}
                    data-testid={`dpr-line-activity-${line.lineId}`}
                    value={line.activity}
                    onChange={(val) => handleUpdateLine(line.lineId, { activity: val })}
                    readOnly={!canEdit}
                    disabled={busy}
                  />
                </div>

                {/* Quantities & Unit */}
                <div className={styles.gridTwoCol}>
                  <div className={styles.inputGroup}>
                    <label className={styles.fieldLabel} htmlFor={`dpr-line-plannedQty-${line.lineId}`}>
                      {t('NLSHelperOpsDprPlannedQtyLabel')}
                    </label>
                    <TextInput
                      id={`dpr-line-plannedQty-${line.lineId}`}
                      type="number"
                      value={line.plannedQty != null ? String(line.plannedQty) : ''}
                      onChange={(val) =>
                        handleUpdateLine(line.lineId, {
                          plannedQty: val === '' ? null : Number(val),
                        })
                      }
                      readOnly={!canEdit}
                      disabled={busy}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.fieldLabel} htmlFor={`dpr-line-doneQty-${line.lineId}`}>
                      {t('NLSHelperOpsDprDoneQtyLabel')}
                    </label>
                    <TextInput
                      id={`dpr-line-doneQty-${line.lineId}`}
                      data-testid={`dpr-line-doneQty-${line.lineId}`}
                      type="number"
                      value={line.doneQty != null ? String(line.doneQty) : ''}
                      onChange={(val) =>
                        handleUpdateLine(line.lineId, {
                          doneQty: Number(val) || 0,
                        })
                      }
                      readOnly={!canEdit}
                      disabled={busy}
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.fieldLabel} htmlFor={`dpr-line-unit-${line.lineId}`}>
                    {t('NLSOpsConDprLineUnit')}
                  </label>
                  <TextInput
                    id={`dpr-line-unit-${line.lineId}`}
                    value={line.unit}
                    onChange={(val) => handleUpdateLine(line.lineId, { unit: val })}
                    readOnly={!canEdit}
                    disabled={busy}
                  />
                </div>

                {/* Photos */}
                <div className={styles.photosSection}>
                  <span className={styles.fieldLabel}>
                    {t('NLSHelperOpsDprPhotosLabel', { count: totalPhotos })}
                  </span>
                  {lineBlobsForThisLine.length > 0 ? (
                    <div className={styles.photoList}>
                      {lineBlobsForThisLine.map((b, pIdx) => (
                        <img
                          key={pIdx}
                          src={b.previewUrl}
                          alt={`${t('NLSHelperOpsDprPhotoAlt') || 'Line photo'} ${pIdx + 1}`}
                          className={styles.photoThumb}
                        />
                      ))}
                    </div>
                  ) : null}
                  {canEdit && totalPhotos < 20 ? (
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleAddPhoto(line.lineId, e)}
                      disabled={busy}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {canEdit ? (
        <Button
          variant="secondary"
          size="sm"
          data-testid="dpr-add-line-btn"
          onClick={handleAddLine}
          disabled={busy}
        >
          {t('NLSHelperOpsDprAddLineBtn')}
        </Button>
      ) : null}

      {/* Action Buttons */}
      {canEdit ? (
        <div className={styles.actionsRow}>
          <Button
            variant="secondary"
            data-testid="dpr-save-draft-btn"
            className={styles.fullWidthBtn}
            onClick={handleSaveDraft}
            disabled={busy}
          >
            {t('NLSHelperOpsDprSaveDraftBtn')}
          </Button>

          {canSubmit ? (
            <Button
              variant="primary"
              data-testid="dpr-submit-btn"
              className={styles.fullWidthBtn}
              onClick={handleSubmit}
              disabled={busy || !isOnline}
            >
              {t('NLSHelperOpsDprSubmitBtn')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <BarcodeScannerShell
        open={Boolean(activeScanLineId)}
        onClose={() => setActiveScanLineId(null)}
        onScan={handleScanPlace}
      />
    </div>
  )
}
